<template>
  <BonusCard
    :id="bonus.id"
    type="cash"
    :paused="rollingStatus === 'paused'"
    :timer="bonusTimer"
    :progress="rollingProgress"
    :steps-progress="stepsProgress"
    :level="bonus.bonusForDepositProgram?.level"
    data-t="cash-bonus-card"
  >
    <template v-if="status === 'done'" #image>
      <img :src="depositClaim" alt="" width="44" height="44" />
    </template>
    <template #subtitle>
      <div class="amount-container">
        <div class="amount">
          <StIcon
            v-if="isEnabledStepsRolling"
            class="lock-icon"
            name="lock-solid"
            :size="16"
          />
          <span :base-size="16" :min-font-size="24" data-t="amount-o737">
            {{ bonusAmountForClaim }}
          </span>
          <StIcon :name="bonusCurrencyIcon" :size="16" />
        </div>
        <div v-if="isEnabledStepsRolling" class="steps-amount">
          <div
            v-if="unlockedAmount"
            class="steps-amount-info"
            data-t="unlocked-amount"
          >
            {{ t('bonuses.unlockedAmount') }}
            {{ format(unlockedAmount) }}
            <StIcon :name="bonusCurrencyIcon" :size="14" />
          </div>
          <div v-else class="steps-amount-info" data-t="until-next-clime">
            {{ t('bonuses.untilNextClaim') }}
            {{ format(untilNextClaimAmount) }}
            <StIcon :name="bonusCurrencyIcon" :size="14" />
          </div>
        </div>
      </div>
    </template>

    <template v-if="status === 'rolling'" #progress-text>
      <div class="rolling-data">
        <span class="wager" data-t="wager-gv4i">
          {{ t('bonuses.wager', { multiplier: rollingMultiplier }) }}
        </span>
        <span class="current-rolling" data-t="current-rolling">
          {{ minMaxRollingText }}
          <StIcon :name="bonusCurrencyIcon" :size="16" />
        </span>
      </div>
    </template>

    <template #actions>
      <div class="actions-wrapper">
        <StButton
          v-if="canPartClaimForDepositBonus"
          class="button"
          type="primary"
          is-pulsing
          :loading="partClaimStatus === 'pending'"
          :disabled="['success', 'error'].includes(partClaimStatus)"
          :label="t('bonuses.takeBonus')"
          data-t="part-rolling-button"
          @click="handlePartClaim"
        />
        <StButton
          v-else
          class="button"
          :type="buttonType"
          :loading="claimStatus === 'pending'"
          :is-pulsing="textContentByStatus.isPulsing"
          :disabled="['success', 'error'].includes(claimStatus)"
          :label="textContentByStatus.buttonText"
          :icon="textContentByStatus.buttonIcon"
          :data-t="`button-${rollingStatus}-o8pq`"
          is-left-icon
          @click="onButtonClick"
        />
      </div>
    </template>
    <LottieClaimAnimation ref="claimAnimation" :is-big="isMobile" />
  </BonusCard>
</template>

<script setup lang="ts">
import { delay } from '@st/utils'
import LottieClaimAnimation from '@st/ui/components/LottieClaimAnimation/LottieClaimAnimation.client.vue'
import type { CashBonus } from '../../types'
import BonusCard from '../BonusCard/BonusCard.vue'
import { useCashBonus } from '../../composables/useCashBonus'
import { useBonusRolling } from '../../composables/useBonusRolling'
import { useBonusStepRolling } from '../../composables/useBonusStepRolling'
import depositClaim from '../../assets/deposit-claim.webp'

interface Props {
  bonus: CashBonus
}
const animation =
  useTemplateRef<InstanceType<typeof LottieClaimAnimation>>('claimAnimation')

const { t } = useI18n()
const { bonus } = defineProps<Props>()
const { isMobile } = usePlatform()
const {
  isCanClaimed,
  bonusTimer,
  textContentByStatus,
  bonusAmountForClaim,
  bonusCurrencyIcon,
  status,
  rollingMultiplier,
  claimStatus,
  handleBonusClaim,
  updateCashBonuses,
} = useCashBonus(
  computed(() => bonus),
  () => animation.value?.runClaimAnimation(),
)

const {
  rollingStatus,
  isRollingStatusNewOrPaused,
  rollingProgress,
  currentRollingAmount,
  fullRollingAmount,
  setCurrentRolling,
} = useBonusRolling(computed(() => bonus.rolling))

const stepRollingData = computed(() => ({
  stepRollingData: bonus.rollingSteps ?? [],
  rollingData: bonus.rolling,
  bonusAmount: bonus.amount,
}))

const {
  unlockedAmount,
  untilNextClaimAmount,
  canPartClaim,
  partClaim,
  partClaimStatus,
  isEnabledStepsRolling,
  stepsProgress,
} = useBonusStepRolling(stepRollingData)

async function handlePartClaim() {
  await partClaim()
  if (partClaimStatus.value === 'success') {
    await delay(300)
    await updateCashBonuses()
  }
}

const canPartClaimForDepositBonus = computed(() => {
  // если бонус без обкатки (необходимо выполнить claim всего бонуса целиком)
  if (!bonus.rolling) return false

  return canPartClaim.value
})

const minMaxRollingText = computed(
  () => `${currentRollingAmount.value} / ${fullRollingAmount.value}`,
)

const buttonType = computed(() =>
  isRollingStatusNewOrPaused.value ? 'secondary' : 'primary',
)

const router = useRouter()
function onButtonClick() {
  if (isRollingStatusNewOrPaused.value) {
    setCurrentRolling()
    return
  }

  if (isCanClaimed.value) {
    handleBonusClaim()
    return
  }

  router.push('/casino')
}

const { format } = useCryptoFormatter()
</script>

<style scoped>
.actions-wrapper {
  position: relative;
  display: flex;
  flex: 1 1 50%;
  min-width: 0;
}

.button {
  flex: 1 1 0;
  min-width: 0;
}

.amount-container {
  display: flex;
  gap: var(--spacing-050);
  align-items: center;
}

.steps-amount {
  margin-left: auto;
  font: var(--mobile-caption-1-regular);
  color: var(--text-primary);
}

.steps-amount-info {
  overflow: hidden;
  display: flex;
  gap: var(--spacing-025);
  align-items: center;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.amount {
  display: flex;
  gap: var(--spacing-025);
  align-items: center;
}

.rolling-data {
  display: flex;
  align-items: center;
  justify-content: space-between;

  font: var(--mobile-caption-1-regular);
  color: var(--text-primary);
}

.current-rolling {
  display: flex;
  gap: var(--spacing-025);
  align-items: center;
}

.lock-icon {
  color: var(--icon-secondary);
}
</style>
